/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal
} from '@angular/core';
import { Router } from '@angular/router';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  concatMap,
  finalize,
  map,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ROUTES } from '~console/app/~shared/constants/routes.constants';
import { ProfileService } from '~console/app/~shared/services/profile.service';
import { ToastService } from '~console/app/~shared/services/toast.service';
import { removeNilProperties } from '~console/app/~shared/utils/utils';
import {
  OrganizationCreationBuildPayloadsParams,
  OrganizationCreationParams,
  OrganizationHeadquartersCreationParams,
  ReferenceDataType,
  SiteOwner
} from '~core/models/organization.models';
import { OrganizationService } from '~console/app/~shared/services/organization.service';
import { ReferenceDataService } from '~core/services/reference-data.service';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { ConstantsService } from '~core/services/constants.service';
import { EstablishmentService } from '~core/services/establishment.service';

@Component({
  selector: 'onboarding-summary',
  standalone: false,
  templateUrl: './onboarding-summary.component.html',
  styleUrl: './onboarding-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingSummaryComponent extends BaseAuthComponent {
  private readonly _profileService = inject(ProfileService);
  private readonly _referenceDataService = inject(ReferenceDataService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _etablishmentService = inject(EstablishmentService);
  private readonly _router = inject(Router);
  private readonly _toastService = inject(ToastService);
  private readonly _constantService = inject(ConstantsService);

  onboardingSummaryValues = signal<null | any>(null);

  onboardingSummary$ = combineLatest([
    this._authFacade.locationFormValues$,
    this._authFacade.billingEquipmentFormValues$,
    this._authFacade.currentUser$
  ]).pipe(
    map(([location, billing, currentUser]) => {
      const summary = {
        location: location?.model,
        billingEquipment: billing?.model,
        currentUser
      };

      this.onboardingSummaryValues.set(summary);

      return summary;
    })
  );

  activities = toSignal(
    this._referenceDataService
      .list({
        type: 'nature_of_activity',
        itemPerPage: 100_000_000
      })
      .pipe(map(res => res.items))
  );

  activity = computed(() => {
    const summary = this.onboardingSummaryValues();
    const activities = this.activities();

    const activityId = summary?.location?.activity;
    if (!activityId || !activities?.length) return null;

    return activities.find(a => a.value === activityId) ?? null;
  });

  hasUserConsented = false;
  isSubmitting = signal(false);

  /* onSubmit(
    items:
      | {
          name: string;
          otherValue?: string;
          type: ReferenceDataType;
          label?: string;
          value?: string;
        }[]
      | null
      | undefined
  ): void {
    const designations = (items || []).filter(
      item => item.name === 'other' && item.otherValue?.trim() !== ''
    );

    const createDesignations$: Observable<unknown> = isEmpty(designations)
      ? of(null)
      : this._referenceDataService.create(
          designations.map(item => ({
            type: item.type,
            label: item.otherValue as string,
            value: item.otherValue as string
          }))
        );

    this.isSubmitting.set(true);

    createDesignations$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError(() => {
          this._toastService.open({
            content: 'Échec de liaison des désignations personnalisées.',
            type: 'error'
          });
          return EMPTY;
        }),
        concatMap(() => this._updateOrganization()),
        tap({
          next: () => {
            this._router.navigateByUrl(ROUTES.home);
            this._authFacade.resetOnboardingForms();
          }
        }),
        catchError(() => {
          this._toastService.open({
            content: "Échec de finalisation de la création de l'organisation.",
            type: 'error'
          });
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  } */

  onSubmit(): void {
    this.isSubmitting.set(true);

    this._updateOrganization()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._router.navigateByUrl(ROUTES.home);
            this._authFacade.resetOnboardingForms();
          }
        }),
        catchError(() => {
          this._toastService.open({
            content: "Échec de finalisation de la création de l'organisation.",
            type: 'error'
          });
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  private _updateOrganization(): Observable<unknown> {
    const summary = this.onboardingSummaryValues();

    if (!summary?.currentUser?.organization?.id) {
      return throwError(() => new Error('Identifiant organisation manquant'));
    }

    const { organizationId, organizationPayload, defaultEstablishmentPayload } =
      this._buildOrganizationCreationPayloads(summary);

    return this._organizationService
      .update(organizationId, organizationPayload)
      .pipe(
        concatMap(() =>
          this._etablishmentService.createDefault(
            organizationId,
            defaultEstablishmentPayload
          )
        ),
        switchMap(() =>
          this._profileService
            .read()
            .pipe(
              tap(currentUser => this._authFacade.patchLoggedUser(currentUser))
            )
        )
      );
  }

  private _formatPhoneNumber(
    phoneNumber: string | undefined
  ): string | undefined {
    if (!phoneNumber?.trim()) {
      this._loggerService.debug('Phone number is empty or not provided');
      return;
    }

    try {
      const parsed = parsePhoneNumberWithError(
        phoneNumber,
        this._constantService.defaultCountry
      );
      return parsed.format('E.164');
    } catch (error) {
      this._loggerService.warn(error);
      return;
    }
  }

  private _mapToBillingEquipments(
    items:
      | { otherValue: string; type: ReferenceDataType; name: string }[]
      | null
      | undefined
  ): { type: string; name: string }[] {
    const validItems = items || [];

    const result = validItems
      .filter(
        item =>
          item?.type != null && (item?.otherValue != null || item?.name != null)
      )
      .map(item => ({
        type: item.type as string,
        name: (item.otherValue || item.name) as string
      }));
    return result;
  }

  private _buildOrganizationCreationPayloads(
    summary: OrganizationCreationBuildPayloadsParams
  ) {
    const formattedPhoneNumber = this._formatPhoneNumber(
      summary.location?.phoneNumber
    );
    const billingEquipments = this._mapToBillingEquipments(
      summary.billingEquipment?.equipments
    );

    const address = removeNilProperties({
      city: summary.location?.city,
      district: summary.location?.district,
      neighborhood: summary.location?.neighborhood,
      location: summary.location?.location,
      latitude: summary.location?.latitude,
      longitude: summary.location?.longitude,
      section: summary.location?.section,
      parcel: summary.location?.parcel,
      lot: summary.location?.lot,
      ilot: summary.location?.ilot
    });

    const siteOwner = removeNilProperties({
      name: summary.location.siteOwnerName,
      nif: summary.location.siteOwnerNif,
      phoneNumber: summary.location.siteOwnerPhoneNumber,
      email: summary.location.siteOwnerEmail
    }) as SiteOwner;

    const organizationPayload = removeNilProperties({
      rccNumber: summary.location?.rccNumber,
      phoneNumber: formattedPhoneNumber,
      email: summary.location?.email,
      cashRegistersNumber: summary.billingEquipment?.cashRegistersNumber,
      ...(billingEquipments.length > 0 ? { billingEquipments } : {})
    }) as OrganizationCreationParams;

    const defaultEstablishmentPayload: OrganizationHeadquartersCreationParams =
      {
        name: summary?.currentUser?.organization?.name,
        ownerPhoneNumber: summary?.location.phoneNumber,
        email: summary.location.email,
        siteOwner: siteOwner,
        address: address
      };

    const organizationId = summary.currentUser?.organization?.id;

    return {
      organizationId,
      organizationPayload,
      defaultEstablishmentPayload
    };
  }

  toggleConsent(): void {
    this.hasUserConsented = !this.hasUserConsented;
  }

  handlePrevStep(): void {
    this._eventService.emit({ type: 'GO_TO_PREVIOUS_STEP' });
  }
}
